import React, { memo, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { ISelectOption } from 'components/forms/Selects'
import { CarrierSelect } from 'apps/fleet-planner/components/atoms'
import { CarrierInlineDisplay } from 'apps/fleet-planner/components/atoms/CarrierInlineDisplay'
import { LockedTooltip } from 'apps/fleet-planner/components/load-details/LockedTooltip'
import { useShipmentApi } from 'apps/fleet-planner/hooks/useShipmentApi'
import * as Types from 'apps/fleet-planner/types'
import { LOAD_FIELDS } from 'apps/fleet-planner/utils/permissions'
import { selectCarriersList } from 'apps/fleet-planner/utils/selectors/carriers'
import { Button, InlineEditContainer, Card } from 'transfix-ui'

interface CarrierInlineEditProps {
  carrier?: Types.ICarrier
  currentEditField?: string
  isLocked?: boolean
  onEditModeRequested: (name?: string) => void
  onUpdate: (changes: Partial<Types.ILoad>) => void
}

const NAME = LOAD_FIELDS.carrier

export const CarrierInlineEdit = memo<CarrierInlineEditProps>(
  ({ carrier: carrierProp, currentEditField, isLocked, onEditModeRequested }) => {
    const { updateShipment } = useShipmentApi()
    const carrierList = useSelector(selectCarriersList)

    const [localChanges, setLocalChanges] = useState<Pick<Types.ILoad, 'motorCarrier'>>({
      motorCarrier: carrierProp as Types.ICarrier,
    })

    // Update state when receiving new props
    useEffect(() => {
      setLocalChanges({ motorCarrier: carrierProp })
    }, [carrierProp])

    const resetLocalChanges = () => {
      setLocalChanges({ motorCarrier: carrierProp })
    }

    const changeHandler = ({ value: motorCarrierId }: ISelectOption<number>) => {
      const matchingCarrier = carrierList.find(c => c.id === motorCarrierId)
      setLocalChanges({
        motorCarrier: matchingCarrier,
      })
    }

    const onSave = () => {
      updateShipment({
        mcNumber: localChanges.motorCarrier?.mcNumber,
        motorCarrierId: localChanges.motorCarrier?.id,
        motorCarrier: localChanges.motorCarrier,
      })
      onEditModeRequested('')
    }

    const onCancel = () => {
      resetLocalChanges()
      onEditModeRequested('')
    }

    const editModeRequestedHandler = (name?: string) => {
      onEditModeRequested(name)
      if (!name) {
        resetLocalChanges()
      }
    }

    const isEditMode = currentEditField === NAME
    return (
      <InlineEditContainer
        isLocked={isLocked}
        lockedIcon={<LockedTooltip className='locked-icon' />}
        className='relative mt4'
        name={NAME}
        dataTest='carrier-inline-edit'
        editModeEnabled={isEditMode}
        editMode={
          <Card className='pa4'>
            <CarrierSelect
              label='Carrier'
              motorCarrierId={localChanges?.motorCarrier?.id}
              onChange={changeHandler}
              defaultCarrier={false}
              required
              large
            />
            <div className='tr'>
              <div className='mt4'>
                <Button color='tertiary' size='large' onClick={onCancel}>
                  Cancel
                </Button>
                <Button
                  color='primary'
                  size='large'
                  onClick={onSave}
                  className='ml2'
                  disabled={!localChanges?.motorCarrier?.id}>
                  Save
                </Button>
              </div>
            </div>
          </Card>
        }
        onEditModeRequested={editModeRequestedHandler}>
        <CarrierInlineDisplay value={carrierProp?.legalName} />
      </InlineEditContainer>
    )
  }
)
